import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { useFeedback } from '@/components/feedback/feedback-context'
import { DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { ReadonlyField } from '@/components/forms/readonly-field'
import { Select } from '@/components/forms/select'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'

import { batchUpdateUserStatus, updateMemberAccount, type AdminMemberAccount } from './api'

interface AdminMemberAccountDrawerProps {
  member: AdminMemberAccount
  onOpenChange: (open: boolean) => void
  open: boolean
}

type MemberAccountFormErrors = Partial<Record<'name', string>>

function roleLabel(role: string, t: (key: string) => string) {
  if (role === 'admin') {
    return t('memberAccounts.role.admin')
  }

  if (role === 'user') {
    return t('memberAccounts.role.user')
  }

  return role.trim() ? role : t('memberAccounts.role.unknown')
}

export function AdminMemberAccountDrawer({
  member,
  onOpenChange,
  open,
}: AdminMemberAccountDrawerProps) {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [name, setName] = useState(member.name)
  const [status, setStatus] = useState<AdminMemberAccount['status']>(member.status)
  const [errors, setErrors] = useState<MemberAccountFormErrors>({})
  const statusOptions = useMemo(
    () => [
      {
        label: t('memberAccounts.status.active'),
        value: 'active',
      },
      {
        label: t('memberAccounts.status.banned'),
        value: 'banned',
      },
    ],
    [t],
  )

  const invalidateAccounts = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-organization-member-accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-organization-tree'] }),
    ])
  const showOperationFeedback = (tone: 'error' | 'success' | 'warning', message: string) => {
    showFeedback({
      message,
      tone,
    })
  }
  const saveMutation = useMutation({
    mutationFn: async () => {
      const nextErrors: MemberAccountFormErrors = {}
      if (!name.trim()) {
        nextErrors.name = t('memberAccounts.drawer.validation.nameRequired')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return { skipped: true }
      }

      setErrors({})
      await updateMemberAccount({
        name,
        userId: member.id,
      })
      const statusResult =
        status !== member.status ? await batchUpdateUserStatus([member.id], status) : null

      return {
        skipped: false,
        statusResult,
      }
    },
    onSuccess: async (result) => {
      if (result.skipped) {
        return
      }
      await invalidateAccounts()
      if (result.statusResult?.code === 'partial_success') {
        showOperationFeedback('warning', t('errors.batchStatusPartial'))
        return
      }
      showOperationFeedback('success', t('success.updateMemberAccount'))
      onOpenChange(false)
    },
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
  })

  return (
    <Drawer
      footer={
        <>
          <Button
            disabled={saveMutation.isPending}
            onClick={() => onOpenChange(false)}
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {t('actions.save')}
          </Button>
        </>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={t('memberAccounts.drawer.titleWithMember', { name: member.name })}
    >
      <DrawerFormRow columns={1}>
        <FormField label={t('memberAccounts.drawer.email')}>
          <ReadonlyField>
            <span className="break-all">{member.email}</span>
          </ReadonlyField>
        </FormField>
        <FormField error={errors.name} label={t('memberAccounts.drawer.name')} required>
          <Input
            aria-label={t('memberAccounts.drawer.name')}
            invalid={Boolean(errors.name)}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('memberAccounts.drawer.namePlaceholder')}
            value={name}
          />
        </FormField>
        <FormField label={t('memberAccounts.drawer.status')}>
          <Select
            aria-label={t('memberAccounts.drawer.status')}
            onValueChange={(value) => setStatus(value as AdminMemberAccount['status'])}
            options={statusOptions}
            value={status}
          />
        </FormField>
        <FormField label={t('memberAccounts.drawer.role')}>
          <div className="flex min-h-[38px] items-center">
            <Badge variant={member.role === 'admin' ? 'info' : 'neutral'}>
              {roleLabel(member.role, t)}
            </Badge>
          </div>
        </FormField>
        <FormField label={t('memberAccounts.drawer.department')}>
          <ReadonlyField className="flex-wrap gap-2">
            {member.departments.length > 0
              ? member.departments.map((department) => (
                  <Badge key={department.id}>{department.name}</Badge>
                ))
              : t('common.noDepartment')}
          </ReadonlyField>
        </FormField>
        <FormField label={t('memberAccounts.drawer.lastActiveAt')}>
          <ReadonlyField>{formatDateTime(member.last_active_at)}</ReadonlyField>
        </FormField>
        <FormField label={t('memberAccounts.drawer.userId')}>
          <ReadonlyField>
            <span className="break-all">{member.id}</span>
          </ReadonlyField>
        </FormField>
      </DrawerFormRow>
    </Drawer>
  )
}
